import { zodResolver } from '@hookform/resolvers/zod';
import {
  CreateUserRequestSchema,
  DESTINATION_HAULS,
  UpdateUserRequestSchema,
  buildEmployeeCode,
  getParentRolesFor,
  isCoverageRole,
  isOperationsDepartmentCode,
  isOperationsRole,
  isSalesRole,
  isUserCoverageDepartmentCode,
  type CreateUserRequest,
  type DestinationHaul,
  type UpdateUserRequest,
  type UserDestinationSlot,
  type UserDetail,
  type UserListItem,
  type UserRole,
  type UserSalesHaul,
  type UserSalesScope,
  USER_ROLES,
  salesHaulRequired,
  OPS_DEPARTMENTS,
  OPS_DEPARTMENT_LABEL,
  type OpsDepartment,
} from '@utazzo/shared';
import {
  UzButton,
  UzDialog,
  UzForm,
  UzFormField,
  UzInput,
  UzModernDatePicker,
  UzSelect,
  UzSpinner,
  UzSwitch,
} from '@utazzo/ui';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';

import {
  useAssignableRoles,
  useCreateUser,
  useEligibleManagers,
  useUpdateUser,
  useUserDetail,
  extractApiMessage,
} from '../use-users';

import { ExitReleaseConfirmDialog } from './ExitReleaseConfirmDialog';
import { UserDestinationsEditor } from './UserDestinationsEditor';

import { useCurrentUser } from '@/features/auth/use-auth';
import { useBranchesList } from '@/features/branches/use-branches';
import { useDepartmentsList } from '@/features/departments/use-departments';
import { useSubBranchesList } from '@/features/sub-branches/use-sub-branches';
import { useTripTypesList } from '@/features/trip-types/use-trip-types';

const ROLE_LABELS: Record<string, string> = {
  ADMIN: 'Administrator',
  SALES_LEAD: 'Sales Lead',
  SALES_AGENT: 'Sales Agent',
  OPS: 'Operations',
  FINANCE: 'Finance',
  HR: 'HR',
  READ_ONLY: 'Read-only',
  CONTENT_EDITOR: 'Content Editor',
  MARKETING_LEAD: 'Marketing Lead',
};

/** YYYY-MM-DD string → local Date for the calendar picker. */
function parseDateOnly(value: string | null | undefined): Date | null {
  if (!value) return null;
  const m = value.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!m) return null;
  const year = Number(m[1]);
  const month = Number(m[2]);
  const day = Number(m[3]);
  const d = new Date(year, month - 1, day);
  return Number.isNaN(d.getTime()) ? null : d;
}

/** Local Date → YYYY-MM-DD for the shared schema / API. */
function toIsoDate(d: Date): string {
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
}

// +91 is fixed for Indian staff: the prefix is shown as a non-editable
// adornment and operators type only the 10-digit local number. Form
// state holds JUST those local digits; the +91 is re-attached on submit
// (toStoredPhone) so the API persists a fully-qualified number.
const IN_DIAL_CODE = '+91';

/** Any stored value (e.g. "+91-9000000001") → up to 10 local digits. */
function toLocalPhone(value: string | null | undefined): string {
  if (!value) return '';
  let digits = value.replace(/\D/g, '');
  // Only strip a country code / trunk 0 when the value is clearly long
  // enough to carry one - never touch a bare 10-digit local number.
  if (digits.length > 10) {
    if (digits.startsWith('0091')) digits = digits.slice(4);
    else if (digits.startsWith('91')) digits = digits.slice(2);
    else if (digits.startsWith('0')) digits = digits.slice(1);
  }
  return digits.slice(0, 10);
}

/** Local digits → stored value with the +91 prefix, or '' when empty. */
function toStoredPhone(value: string | null | undefined): string {
  const digits = toLocalPhone(value);
  return digits ? `${IN_DIAL_CODE}${digits}` : '';
}

/**
 * Phone input with a fixed +91 prefix. Shared by the create + edit
 * forms; the RHF `phone` field holds only the local digits (or
 * undefined), so there is no prefix to round-trip and re-parse.
 */
const PhoneField = ({ pending }: { pending: boolean }): React.JSX.Element => (
  <UzFormField name="phone">
    {({ field, error }) => (
      <UzInput
        name={field.name}
        ref={field.ref}
        onBlur={field.onBlur}
        value={toLocalPhone(field.value as string | null | undefined)}
        onChange={(e) => field.onChange(toLocalPhone(e.target.value) || undefined)}
        type="tel"
        inputMode="numeric"
        maxLength={10}
        leftAdornment="+91"
        className="pl-12"
        label="Phone"
        placeholder="98765 43210"
        error={error}
        disabled={pending}
      />
    )}
  </UzFormField>
);

/**
 * CallerDesk member/virtual number. Drives click-to-call (the agent's call
 * goes out from this number, never their personal phone) and doubles as an
 * alternate login identifier (login by email OR this number).
 */
const CallerDeskNumberField = ({ pending }: { pending: boolean }): React.JSX.Element => (
  <UzFormField name="callerDeskNumber">
    {({ field, error }) => (
      <UzInput
        name={field.name}
        ref={field.ref}
        onBlur={field.onBlur}
        value={(field.value as string | null | undefined) ?? ''}
        onChange={(e) => field.onChange(e.target.value.trim() || undefined)}
        type="tel"
        inputMode="numeric"
        maxLength={20}
        label="CallerDesk number"
        placeholder="09111111890"
        hint="Used for click-to-call and login. Get it from CallerDesk (member number)."
        error={error}
        disabled={pending}
      />
    )}
  </UzFormField>
);

const OPS_DESK_OPTION_PREFIX = 'ops-desk:';
const opsDeskOptionValue = (desk: OpsDepartment): string => `${OPS_DESK_OPTION_PREFIX}${desk}`;
const opsDeskFromOptionValue = (value: string | undefined): OpsDepartment | null => {
  if (!value || !value.startsWith(OPS_DESK_OPTION_PREFIX)) return null;
  const desk = value.slice(OPS_DESK_OPTION_PREFIX.length);
  return (OPS_DEPARTMENTS as readonly string[]).includes(desk) ? (desk as OpsDepartment) : null;
};

const UserHrFields = ({ pending }: { pending: boolean }): React.JSX.Element => {
  // The Administrator flag is admin-only. Finance manages non-admin users and
  // must never mark anyone an admin (mirrors the API guardrail), so the toggle
  // is hidden for the FINANCE role.
  const me = useCurrentUser();
  const canSetAdmin = me?.role !== 'FINANCE';
  return (
    <>
      <UzFormField name="dateOfBirth">
        {({ field, error }) => (
          <UzModernDatePicker
            label="Date of birth"
            placeholder="Pick date of birth"
            value={parseDateOnly(field.value as string | null | undefined)}
            onValueChange={(d) => field.onChange(d ? toIsoDate(d) : null)}
            fromYear={1950}
            toYear={new Date().getFullYear()}
            error={error}
            disabled={pending}
          />
        )}
      </UzFormField>
      <UzFormField name="dateOfJoining">
        {({ field, error }) => (
          <UzModernDatePicker
            label="Date of joining"
            placeholder="Pick date of joining"
            value={parseDateOnly(field.value as string | null | undefined)}
            onValueChange={(d) => field.onChange(d ? toIsoDate(d) : null)}
            fromYear={1990}
            toYear={new Date().getFullYear() + 1}
            error={error}
            disabled={pending}
          />
        )}
      </UzFormField>
      <UzFormField name="dateOfExit">
        {({ field, error }) => (
          <UzModernDatePicker
            label="Date of exit"
            placeholder="Pick date of exit"
            hint="Leave blank while the user is active."
            value={parseDateOnly(field.value as string | null | undefined)}
            onValueChange={(d) => field.onChange(d ? toIsoDate(d) : null)}
            fromYear={1990}
            toYear={new Date().getFullYear() + 15}
            error={error}
            disabled={pending}
          />
        )}
      </UzFormField>
      {canSetAdmin && (
        <UzFormField name="isAdmin">
          {({ field }) => (
            <UzSwitch
              label="Administrator"
              description="Marks this user as an admin. Picking the ADMIN role also enables this."
              checked={Boolean(field.value)}
              onCheckedChange={field.onChange}
              disabled={pending}
            />
          )}
        </UzFormField>
      )}
      <UzFormField name="autoAssignLeads">
        {({ field }) => (
          <UzSwitch
            label="Auto lead assignment"
            description="Off keeps new leads from reaching this user on their own. An admin can still assign leads to them by hand."
            checked={field.value !== false}
            onCheckedChange={field.onChange}
            disabled={pending}
          />
        )}
      </UzFormField>
    </>
  );
};

// 2026-06-26 ADR-0057. Sales coverage is now FIRST-CLASS on the user
// (scope[], haul[], tripTypes[] all persisted directly). The 3
// destination slots are ordered: slot 1 = primary required, 2 + 3 =
// optional. The form section below replaces the old
// DestinationsMultiPicker.
const HAUL_LABELS: Record<DestinationHaul, string> = {
  SHORT_HAUL: 'Short Haul',
  LONG_HAUL: 'Long Haul',
};
const SCOPE_LABELS: Record<UserSalesScope, string> = {
  INTERNATIONAL: 'International',
  DOMESTIC: 'Domestic',
};
const SCOPE_VALUES: ReadonlyArray<UserSalesScope> = ['INTERNATIONAL', 'DOMESTIC'];
const HAUL_VALUES: ReadonlyArray<UserSalesHaul> = DESTINATION_HAULS;

const EMPTY_SALES_COVERAGE = {
  salesScopes: [] as UserSalesScope[],
  salesHauls: [] as UserSalesHaul[],
  salesTripTypeIds: [] as string[],
  destinations: [] as UserDestinationSlot[],
};

interface UserFormDrawerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  mode: 'create' | 'edit';
  user?: UserDetail | UserListItem | null;
}

export const UserFormDrawer = ({
  open,
  onOpenChange,
  mode,
  user,
}: UserFormDrawerProps): React.JSX.Element => {
  const isCreate = mode === 'create';
  const createUser = useCreateUser();
  const updateUser = useUpdateUser();
  const pending = createUser.isPending || updateUser.isPending;
  // Coverage (salesScopes / hauls / tripTypes / destinations) is not reliably
  // carried on the lightweight list row, so fetch the authoritative UserDetail
  // when the drawer opens for edit and hydrate the form from that. Without this
  // the Sales-coverage section rendered blank intermittently - it only showed
  // when the list cache happened to already hold the enriched row.
  const userDetailQuery = useUserDetail(open && !isCreate ? (user?.id ?? null) : null);
  // Staged update payload held back when dateOfExit crosses into today-or-past.
  // The release-preview dialog confirms before we actually save + release.
  const [pendingExitPayload, setPendingExitPayload] = useState<UpdateUserRequest | null>(null);

  // Branch + Department master pickers. Active rows only, large page
  // size so the dropdown shows the entire master in one fetch.
  const branchesQuery = useBranchesList({
    page: 1,
    pageSize: 200,
    sortBy: 'sortOrder',
    sortDir: 'asc',
    isActive: true,
  });
  const departmentsQuery = useDepartmentsList({
    page: 1,
    pageSize: 200,
    sortBy: 'sortOrder',
    sortDir: 'asc',
    isActive: true,
  });
  const subBranchesQuery = useSubBranchesList({
    page: 1,
    pageSize: 200,
    sortBy: 'sortOrder',
    sortDir: 'asc',
    isActive: true,
  });
  const branchOptions = useMemo(
    () => (branchesQuery.data?.data ?? []).map((b) => ({ value: b.id, label: b.name })),
    [branchesQuery.data],
  );
  // Operations desks (2026-10-28, folded into the Department list 2026-10-30):
  // the three desks sit in the same dropdown as every other department, as
  // "Operations - Flight" etc. Picking one stores the Operations master as the
  // department plus the desk; picking anything else clears the desk.
  const operationsDepartmentId = useMemo(
    () => (departmentsQuery.data?.data ?? []).find((d) => isOperationsDepartmentCode(d.code))?.id,
    [departmentsQuery.data],
  );
  const departmentOptions = useMemo(() => {
    const rows = departmentsQuery.data?.data ?? [];
    const deskOptions = OPS_DEPARTMENTS.map((d) => ({
      value: opsDeskOptionValue(d),
      label: `Operations - ${OPS_DEPARTMENT_LABEL[d]}`,
    }));
    const out: Array<{ value: string; label: string }> = [];
    let deskInserted = false;
    for (const d of rows) {
      out.push({ value: d.id, label: d.name });
      if (!deskInserted && isOperationsDepartmentCode(d.code)) {
        out.push(...deskOptions);
        deskInserted = true;
      }
    }
    if (!deskInserted) out.push(...deskOptions);
    return out;
  }, [departmentsQuery.data]);
  const departmentCodeById = useMemo(
    () => new Map((departmentsQuery.data?.data ?? []).map((d) => [d.id, d.code] as const)),
    [departmentsQuery.data],
  );

  // Two separate forms keyed by mode - keeps the resolver and types honest.
  const createForm = useForm<CreateUserRequest>({
    resolver: zodResolver(CreateUserRequestSchema),
    defaultValues: {
      email: '',
      fullName: '',
      phone: undefined,
      callerDeskNumber: undefined,
      employeeCode: '',
      dateOfBirth: undefined,
      dateOfJoining: undefined,
      dateOfExit: undefined,
      isAdmin: false,
      autoAssignLeads: true,
      role: 'SALES_AGENT' as UserRole,
      customRoleId: undefined,
      reportingManagerId: undefined,
      branchId: undefined,
      departmentId: undefined,
      subBranchId: undefined,
      // 2026-06-26 ADR-0057. Sales coverage defaults to empty
      // arrays - matches the "not classified yet" state. Operators
      // fill them in via the Sales coverage section below.
      salesScopes: [],
      salesHauls: [],
      salesTripTypeIds: [],
      destinations: [],
    },
  });
  const updateForm = useForm<UpdateUserRequest>({
    resolver: zodResolver(UpdateUserRequestSchema),
    defaultValues: {},
  });
  const employeeCodeManuallyEditedRef = useRef(false);

  const watchedCreateDepartmentId = createForm.watch('departmentId');
  const watchedUpdateDepartmentId = updateForm.watch('departmentId');
  const watchedDepartmentId = (isCreate ? watchedCreateDepartmentId : watchedUpdateDepartmentId) as
    | string
    | null
    | undefined;
  const activeDepartmentCode = watchedDepartmentId
    ? (departmentCodeById.get(watchedDepartmentId) ?? null)
    : null;

  const tripTypesQuery = useTripTypesList({
    page: 1,
    pageSize: 100,
    sortBy: 'sortOrder',
    sortDir: 'asc',
    isActive: true,
  });
  const tripTypeOptionsForPanel = useMemo(
    () => (tripTypesQuery.data?.data ?? []).map((t) => ({ value: t.id, label: t.name })),
    [tripTypesQuery.data],
  );

  const applySalesCoverageToForm = (next: typeof EMPTY_SALES_COVERAGE): void => {
    if (isCreate) {
      createForm.setValue('salesScopes', next.salesScopes, { shouldDirty: true });
      createForm.setValue('salesHauls', next.salesHauls, { shouldDirty: true });
      createForm.setValue('salesTripTypeIds', next.salesTripTypeIds, { shouldDirty: true });
      createForm.setValue('destinations', next.destinations, { shouldDirty: true });
    } else {
      updateForm.setValue('salesScopes', next.salesScopes, { shouldDirty: true });
      updateForm.setValue('salesHauls', next.salesHauls, { shouldDirty: true });
      updateForm.setValue('salesTripTypeIds', next.salesTripTypeIds, { shouldDirty: true });
      updateForm.setValue('destinations', next.destinations, { shouldDirty: true });
    }
  };

  // Reset when the drawer opens / user changes.
  useEffect(() => {
    if (!open) return;
    if (isCreate) {
      employeeCodeManuallyEditedRef.current = false;
      createForm.reset({
        email: '',
        fullName: '',
        phone: undefined,
        employeeCode: '',
        dateOfBirth: undefined,
        dateOfJoining: undefined,
        dateOfExit: undefined,
        isAdmin: false,
        autoAssignLeads: true,
        role: 'SALES_AGENT',
        reportingManagerId: undefined,
        branchId: undefined,
        departmentId: undefined,
        subBranchId: undefined,
        salesScopes: [],
        salesHauls: [],
        salesTripTypeIds: [],
        destinations: [],
      });
    } else {
      // Prefer the authoritative detail (has full coverage); fall back to the
      // list row while it loads so the non-coverage fields fill immediately.
      const sourceUser = userDetailQuery.data ?? user;
      if (!sourceUser) return;
      // Pre-select a custom (non-system) role if one is assigned, so editing a
      // custom-role user shows that role, not the READ_ONLY enum base. A custom
      // role is one whose code isn't a system enum value.
      const systemRoleCodes = new Set(USER_ROLES.map((r) => r.toLowerCase()));
      const customAssignedRole =
        'assignedRoles' in sourceUser && Array.isArray(sourceUser.assignedRoles)
          ? sourceUser.assignedRoles.find((r) => !systemRoleCodes.has(r.code))
          : undefined;
      const hydratedManagerId =
        'reportingManager' in sourceUser && sourceUser.reportingManager
          ? sourceUser.reportingManager.id
          : null;
      const hydratedBranchId =
        'branch' in sourceUser && sourceUser.branch ? sourceUser.branch.id : null;
      const hydratedDepartmentId =
        'department' in sourceUser && sourceUser.department ? sourceUser.department.id : null;
      const hydratedSubBranchId =
        'subBranch' in sourceUser && sourceUser.subBranch ? sourceUser.subBranch.id : null;
      // 2026-06-26 ADR-0057. Hydrate the new coverage fields from
      // the detail response. The destination list now carries
      // `priority` so we project it back into the slot form state.
      const hydratedDestinations: UserDestinationSlot[] =
        'destinations' in sourceUser && sourceUser.destinations
          ? sourceUser.destinations.map((d) => ({
              destinationId: d.id,
              priority: d.priority,
              marketingLeadCapacity: d.marketingLeadCapacity ?? null,
              lpsLeadCapacity: d.lpsLeadCapacity ?? null,
            }))
          : [];
      const hydratedScopes =
        'salesScopes' in sourceUser && sourceUser.salesScopes ? [...sourceUser.salesScopes] : [];
      const hydratedHauls =
        'salesHauls' in sourceUser && sourceUser.salesHauls ? [...sourceUser.salesHauls] : [];
      const hydratedTripTypeIds =
        'salesTripTypes' in sourceUser && sourceUser.salesTripTypes
          ? sourceUser.salesTripTypes.map((t) => t.id)
          : [];
      updateForm.reset({
        fullName: sourceUser.fullName,
        phone: sourceUser.phone ? toLocalPhone(sourceUser.phone) : undefined,
        callerDeskNumber: sourceUser.callerDeskNumber ?? undefined,
        employeeCode: sourceUser.employeeCode ?? '',
        dateOfBirth: sourceUser.dateOfBirth ?? null,
        dateOfJoining: sourceUser.dateOfJoining ?? null,
        dateOfExit: sourceUser.dateOfExit ?? null,
        isAdmin: sourceUser.isAdmin,
        autoAssignLeads: sourceUser.autoAssignLeads !== false,
        opsDepartment: sourceUser.opsDepartment ?? null,
        role: sourceUser.role,
        customRoleId: customAssignedRole?.id,
        reportingManagerId: hydratedManagerId,
        branchId: hydratedBranchId,
        departmentId: hydratedDepartmentId ?? undefined,
        subBranchId: hydratedSubBranchId,
        salesScopes: hydratedScopes,
        salesHauls: hydratedHauls,
        salesTripTypeIds: hydratedTripTypeIds,
        destinations: hydratedDestinations,
      });
    }
  }, [open, isCreate, user, userDetailQuery.data, createForm, updateForm]);

  // Sub-branches are scoped to the picked Branch: the dropdown only
  // offers sub-branches whose parent is the selected branch, and the
  // selection is cleared when the operator switches branches.
  const watchedCreateBranchId = createForm.watch('branchId');
  const watchedUpdateBranchId = updateForm.watch('branchId');
  const watchedBranchId = (isCreate ? watchedCreateBranchId : watchedUpdateBranchId) as
    | string
    | null
    | undefined;
  const subBranchOptions = useMemo(
    () =>
      (subBranchesQuery.data?.data ?? [])
        .filter((sb) => Boolean(watchedBranchId) && sb.branchId === watchedBranchId)
        .map((sb) => ({ value: sb.id, label: sb.name })),
    [subBranchesQuery.data, watchedBranchId],
  );

  // Clear a sub-branch that no longer belongs to the picked branch.
  // Guarded on the loaded sub-branch list so the initial edit hydration
  // (branch + sub-branch set together) is never wrongly wiped.
  useEffect(() => {
    if (!open || !subBranchesQuery.data) return;
    const current = (
      isCreate ? createForm.getValues('subBranchId') : updateForm.getValues('subBranchId')
    ) as string | null | undefined;
    if (!current) return;
    const stillValid = subBranchesQuery.data.data.some(
      (sb) => sb.id === current && sb.branchId === watchedBranchId,
    );
    if (!stillValid) {
      if (isCreate) createForm.setValue('subBranchId', undefined);
      else updateForm.setValue('subBranchId', null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [watchedBranchId, subBranchesQuery.data]);

  // Role-dependent manager dropdown plumbing.
  const watchedCreateRole = createForm.watch('role');
  const watchedUpdateRole = updateForm.watch('role');
  const watchedCreateCustomRoleId = createForm.watch('customRoleId');
  const watchedUpdateCustomRoleId = updateForm.watch('customRoleId');

  // The Primary-role dropdown is sourced from the Roles MASTER (system + custom
  // active roles) so any role created there appears here with its human name -
  // no more hardcoded CAPITAL_UNDERSCORE enum. A system role sets the enum as
  // before (sales etc. untouched); a custom role sets the enum to a READ_ONLY
  // base and is carried as the authoritative grant via `customRoleId`.
  // Role options come from the users API (ADMIN + FINANCE), not the ADMIN-only
  // /roles API, so finance can populate the dropdown. The server already drops
  // the ADMIN role for a finance caller.
  const rolesQuery = useAssignableRoles();
  const masterRoles = useMemo(
    () => (rolesQuery.data ?? []).filter((r) => r.isActive),
    [rolesQuery.data],
  );
  const roleById = useMemo(() => new Map(masterRoles.map((r) => [r.id, r])), [masterRoles]);
  const roleByCode = useMemo(() => new Map(masterRoles.map((r) => [r.code, r])), [masterRoles]);
  const roleSelectOptions = useMemo(
    () =>
      masterRoles
        .slice()
        .sort((a, b) => Number(b.isSystem) - Number(a.isSystem) || a.name.localeCompare(b.name))
        .map((r) => ({ value: r.code, label: r.name })),
    [masterRoles],
  );
  /** The option code currently selected, from the enum role + customRoleId. */
  const roleCodeFor = (
    role: string | null | undefined,
    customRoleId: string | null | undefined,
  ): string =>
    customRoleId ? (roleById.get(customRoleId)?.code ?? '') : role ? role.toLowerCase() : '';
  /** Map a picked option code -> { role enum, customRoleId }. */
  const resolveRolePick = (code: string): { role: UserRole; customRoleId: string | null } => {
    const r = roleByCode.get(code);
    if (r && !r.isSystem) return { role: 'READ_ONLY', customRoleId: r.id };
    return { role: code.toUpperCase() as UserRole, customRoleId: null };
  };
  const watchedCreatePhone = createForm.watch('phone');
  const watchedCreateDateOfJoining = createForm.watch('dateOfJoining');
  const previewEmployeeCode = useMemo(
    () =>
      buildEmployeeCode({
        phone: watchedCreatePhone,
        dateOfJoining: watchedCreateDateOfJoining,
      }),
    [watchedCreatePhone, watchedCreateDateOfJoining],
  );

  // Auto-fill employee code on create until the operator edits it manually.
  useEffect(() => {
    if (!isCreate || employeeCodeManuallyEditedRef.current) return;
    const next = previewEmployeeCode ?? '';
    if ((createForm.getValues('employeeCode') ?? '') !== next) {
      createForm.setValue('employeeCode', next, { shouldValidate: false });
    }
  }, [isCreate, previewEmployeeCode, createForm]);

  const watchedRole: UserRole | undefined = isCreate
    ? watchedCreateRole
    : (watchedUpdateRole as UserRole | undefined);
  // The panel follows the department family, and a sales role always gets
  // it: for a seller all four coverage fields are required (round-robin
  // routes leads on them), so they must be reachable whatever the department.
  const coverageRequired = isSalesRole(watchedRole);
  // Ops roles (2026-10-30) pick the same scope / haul / destinations, all
  // optional and uncapped: the desk rotation prefers a match on any one.
  const opsCoverage = isOperationsRole(watchedRole);
  const showSalesCoverage =
    isUserCoverageDepartmentCode(activeDepartmentCode) || isCoverageRole(watchedRole);
  const eligibleManagers = useEligibleManagers(watchedRole ?? null);
  const eligibleManagersError =
    extractApiMessage(eligibleManagers.error) ?? 'Could not load reporting managers';
  const parentRoleLabel = useMemo(() => {
    if (!watchedRole) return null;
    const parents = getParentRolesFor(watchedRole);
    if (parents.length === 0) return null;
    return parents.map((r) => ROLE_LABELS[r] ?? r).join(' / ');
  }, [watchedRole]);

  const managerOptions = useMemo(() => {
    const opts = (eligibleManagers.data ?? []).map((m) => ({
      value: m.id,
      label: `${m.fullName} (${m.email})`,
    }));
    // Always include the currently-assigned manager as an option, even before
    // the eligible-managers query resolves (or if they've since gone inactive):
    // the Radix Select renders the selected label from a MATCHING option, so
    // without this the field showed blank until the async list happened to be
    // cached (the intermittent "1-2 times visible" behaviour).
    if (!isCreate && user && 'reportingManager' in user && user.reportingManager) {
      const rm = user.reportingManager;
      if (!opts.some((o) => o.value === rm.id)) {
        opts.unshift({ value: rm.id, label: rm.fullName });
      }
    }
    return opts;
  }, [eligibleManagers.data, isCreate, user]);

  // Note: clearing reportingManagerId on a role switch is handled in the role
  // Select's onValueChange (user-initiated only), NOT in a watchedRole effect -
  // an effect fired on initial hydration (when the reset set the role) and wiped
  // the just-hydrated manager, rendering it blank.

  // Keep isAdmin aligned when the operator picks the ADMIN role.
  useEffect(() => {
    if (!open || !watchedRole) return;
    if (watchedRole === 'ADMIN') {
      if (isCreate) createForm.setValue('isAdmin', true);
      else updateForm.setValue('isAdmin', true);
    }
  }, [open, watchedRole, isCreate, createForm, updateForm]);

  // Drop sales coverage fields when the department is not a sales family.
  useEffect(() => {
    if (!open || showSalesCoverage) return;
    applySalesCoverageToForm(EMPTY_SALES_COVERAGE);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, showSalesCoverage]);

  // The Department dropdown carries both the master rows and the ops desks.
  const watchedCreateOpsDesk = createForm.watch('opsDepartment');
  const watchedUpdateOpsDesk = updateForm.watch('opsDepartment');
  const departmentSelectValue = (
    departmentId: string | null | undefined,
    opsDesk: OpsDepartment | null | undefined,
  ): string | undefined => (opsDesk ? opsDeskOptionValue(opsDesk) : (departmentId ?? undefined));
  const onDepartmentSelect = (value: string): void => {
    const desk = opsDeskFromOptionValue(value);
    // Department is mandatory: an empty string fails the UUID check and
    // surfaces the "required" error instead of silently clearing it.
    const departmentId = desk ? (operationsDepartmentId ?? '') : value;
    if (isCreate) {
      createForm.setValue('departmentId', departmentId, {
        shouldDirty: true,
        shouldValidate: true,
      });
      createForm.setValue('opsDepartment', desk, { shouldDirty: true, shouldValidate: false });
    } else {
      updateForm.setValue('departmentId', departmentId, {
        shouldDirty: true,
        shouldValidate: true,
      });
      updateForm.setValue('opsDepartment', desk, { shouldDirty: true, shouldValidate: false });
    }
  };

  const assertSalesCoverageForSubmit = (
    _departmentCode: string | null,
    _destinations: UserDestinationSlot[] | undefined,
  ): boolean => {
    // Primary destination (P1) is optional for sales users - nothing to block on.
    // (If any destination is picked, the request schema still requires one P1.)
    return true;
  };

  const onCreate = createForm.handleSubmit(async (values) => {
    const departmentCode = values.departmentId
      ? (departmentCodeById.get(values.departmentId) ?? null)
      : null;
    if (!assertSalesCoverageForSubmit(departmentCode, values.destinations)) return;

    const salesFields =
      isUserCoverageDepartmentCode(departmentCode) || isCoverageRole(values.role ?? watchedRole)
        ? {
            salesScopes: values.salesScopes ?? [],
            salesHauls: values.salesHauls ?? [],
            salesTripTypeIds: values.salesTripTypeIds ?? [],
            destinations: values.destinations ?? [],
          }
        : EMPTY_SALES_COVERAGE;

    const payload: CreateUserRequest = {
      ...values,
      phone: values.phone?.trim() ? values.phone : undefined,
      callerDeskNumber: values.callerDeskNumber?.trim()
        ? values.callerDeskNumber.trim()
        : undefined,
      employeeCode: values.employeeCode?.trim() ? values.employeeCode.trim() : undefined,
      dateOfBirth: values.dateOfBirth || undefined,
      dateOfJoining: values.dateOfJoining || undefined,
      dateOfExit: values.dateOfExit || undefined,
      isAdmin: values.isAdmin ?? values.role === 'ADMIN',
      autoAssignLeads: values.autoAssignLeads ?? true,
      opsDepartment: values.opsDepartment ?? null,
      reportingManagerId: values.reportingManagerId || undefined,
      branchId: values.branchId || undefined,
      departmentId: values.departmentId,
      subBranchId: values.subBranchId || undefined,
      ...salesFields,
    };
    await createUser.mutateAsync(payload);
    onOpenChange(false);
  });

  const onUpdate = updateForm.handleSubmit(async (values) => {
    if (!user) return;
    const departmentCode = (() => {
      const id =
        values.departmentId !== undefined && values.departmentId !== null
          ? values.departmentId
          : 'department' in user && user.department
            ? user.department.id
            : null;
      return id ? (departmentCodeById.get(id) ?? null) : null;
    })();
    if (!assertSalesCoverageForSubmit(departmentCode, values.destinations)) return;

    const salesFields =
      isUserCoverageDepartmentCode(departmentCode) || isCoverageRole(values.role ?? watchedRole)
        ? {
            salesScopes: values.salesScopes ?? [],
            salesHauls: values.salesHauls ?? [],
            salesTripTypeIds: values.salesTripTypeIds ?? [],
            destinations: values.destinations ?? [],
          }
        : EMPTY_SALES_COVERAGE;

    const payload: UpdateUserRequest = {
      fullName: values.fullName,
      phone: toStoredPhone(values.phone) || null,
      callerDeskNumber: values.callerDeskNumber?.trim() ? values.callerDeskNumber.trim() : null,
      employeeCode: values.employeeCode === '' ? null : values.employeeCode,
      dateOfBirth: values.dateOfBirth === '' ? null : values.dateOfBirth,
      dateOfJoining: values.dateOfJoining === '' ? null : values.dateOfJoining,
      dateOfExit: values.dateOfExit === '' ? null : values.dateOfExit,
      isAdmin: values.isAdmin,
      autoAssignLeads: values.autoAssignLeads,
      opsDepartment: values.opsDepartment ?? null,
      role: values.role,
      customRoleId: values.customRoleId ?? null,
      reportingManagerId: values.reportingManagerId ?? null,
      branchId: values.branchId === '' || values.branchId === undefined ? null : values.branchId,
      departmentId: values.departmentId || undefined,
      subBranchId:
        values.subBranchId === '' || values.subBranchId === undefined ? null : values.subBranchId,
      ...salesFields,
    };

    // Detect dateOfExit crossing into today-or-past (from null or a future
    // date). That transition releases the user's leads server-side, so stage
    // the payload and confirm via the release-preview dialog first. Future
    // dates set during handover do NOT trigger this - they save straight.
    const prevExit = 'dateOfExit' in user ? user.dateOfExit : null;
    const prevExitMs = prevExit ? new Date(prevExit).getTime() : null;
    const nextExitMs = payload.dateOfExit ? new Date(payload.dateOfExit).getTime() : null;
    const wasFutureOrNull = prevExitMs === null || prevExitMs > Date.now();
    const nowIsPastExit = nextExitMs !== null && nextExitMs <= Date.now();
    if (wasFutureOrNull && nowIsPastExit) {
      setPendingExitPayload(payload);
      return;
    }

    await updateUser.mutateAsync({ id: user.id, payload });
    onOpenChange(false);
  });

  // ----- Sales coverage state lifters (shared between create + edit) -----
  // 2026-06-26 ADR-0057. The 4 coverage fields are written through
  // these helpers so the create / edit paths stay in sync; the
  // SalesCoveragePanel below is mode-agnostic.
  const watchScopes = (): UserSalesScope[] =>
    (isCreate
      ? (createForm.watch('salesScopes') ?? [])
      : ((updateForm.watch('salesScopes') as UserSalesScope[] | undefined) ??
        [])) as UserSalesScope[];
  const setScopes = (next: UserSalesScope[]): void => {
    if (isCreate) createForm.setValue('salesScopes', next, { shouldDirty: true });
    else updateForm.setValue('salesScopes', next, { shouldDirty: true });
  };
  const watchHauls = (): UserSalesHaul[] =>
    (isCreate
      ? (createForm.watch('salesHauls') ?? [])
      : ((updateForm.watch('salesHauls') as UserSalesHaul[] | undefined) ?? [])) as UserSalesHaul[];
  const setHauls = (next: UserSalesHaul[]): void => {
    if (isCreate) createForm.setValue('salesHauls', next, { shouldDirty: true });
    else updateForm.setValue('salesHauls', next, { shouldDirty: true });
  };
  const watchTripTypeIds = (): string[] =>
    (isCreate
      ? (createForm.watch('salesTripTypeIds') ?? [])
      : ((updateForm.watch('salesTripTypeIds') as string[] | undefined) ?? [])) as string[];
  const setTripTypeIds = (next: string[]): void => {
    if (isCreate) createForm.setValue('salesTripTypeIds', next, { shouldDirty: true });
    else updateForm.setValue('salesTripTypeIds', next, { shouldDirty: true });
  };
  const watchDestinations = (): UserDestinationSlot[] =>
    (isCreate
      ? (createForm.watch('destinations') ?? [])
      : ((updateForm.watch('destinations') as UserDestinationSlot[] | undefined) ??
        [])) as UserDestinationSlot[];
  const setDestinations = (next: UserDestinationSlot[]): void => {
    if (isCreate) createForm.setValue('destinations', next, { shouldDirty: true });
    else updateForm.setValue('destinations', next, { shouldDirty: true });
  };

  const savedDestinationLabels = useMemo(() => {
    if (!user || !('destinations' in user) || !user.destinations) return {};
    return Object.fromEntries(user.destinations.map((d) => [d.id, d.name]));
  }, [user]);

  const destinationFieldError = isCreate
    ? createForm.formState.errors.destinations?.message
    : updateForm.formState.errors.destinations?.message;
  const coverageErrors = isCreate ? createForm.formState.errors : updateForm.formState.errors;
  const scopesFieldError = coverageErrors.salesScopes?.message;
  const haulsFieldError = coverageErrors.salesHauls?.message;
  const tripTypesFieldError = coverageErrors.salesTripTypeIds?.message;

  return (
    <>
      {/* 2026-10-09. A wide popup instead of the side drawer: the form is
          long (account, HR, role, coverage), so two columns read better. */}
      <UzDialog
        open={open}
        onOpenChange={onOpenChange}
        size="full"
        title={isCreate ? 'New user' : `Edit ${user?.fullName ?? 'user'}`}
        description={
          isCreate
            ? 'The new user will receive a password reset link by email. Their initial password is server-generated and never stored in plain text.'
            : 'Email cannot be changed here. Use a dedicated email-change flow when that ships.'
        }
        footer={
          <div className="flex justify-end gap-2">
            <UzButton variant="ghost" onClick={() => onOpenChange(false)} disabled={pending}>
              Cancel
            </UzButton>
            <UzButton onClick={isCreate ? onCreate : onUpdate} loading={pending}>
              {isCreate ? 'Create user' : 'Save changes'}
            </UzButton>
          </div>
        }
      >
        {isCreate ? (
          <UzForm form={createForm} onSubmit={onCreate}>
            <div className="grid gap-x-8 gap-y-4 lg:grid-cols-2">
              <div className="space-y-4">
                <h3 className="text-xs font-semibold tracking-wide text-zinc-500 uppercase">
                  Account &amp; profile
                </h3>
                <UzFormField name="email">
                  {({ field, error }) => (
                    <UzInput
                      {...field}
                      type="email"
                      label="Work email"
                      placeholder="alex@utazzo.com"
                      error={error}
                      required
                      disabled={pending}
                    />
                  )}
                </UzFormField>
                <UzFormField name="fullName">
                  {({ field, error }) => (
                    <UzInput
                      {...field}
                      label="Full name"
                      placeholder="Alex Mehta"
                      error={error}
                      required
                      disabled={pending}
                    />
                  )}
                </UzFormField>
                <PhoneField pending={pending} />
                <CallerDeskNumberField pending={pending} />
                <UzFormField name="employeeCode">
                  {({ field, error }) => (
                    <UzInput
                      {...field}
                      value={field.value ?? ''}
                      onChange={(e) => {
                        employeeCodeManuallyEditedRef.current = true;
                        field.onChange(e);
                      }}
                      label="Employee code"
                      placeholder="UTZ010726/9875623689"
                      hint="Auto-filled from joining date (or today) and phone. Editable before save."
                      error={error}
                      disabled={pending}
                    />
                  )}
                </UzFormField>
                <UserHrFields pending={pending} />
              </div>
              <div className="space-y-4">
                <h3 className="text-xs font-semibold tracking-wide text-zinc-500 uppercase">
                  Role &amp; coverage
                </h3>
                <UzFormField name="role">
                  {({ error }) => (
                    <UzSelect<string>
                      name="role"
                      label="Primary role"
                      required
                      options={roleSelectOptions}
                      value={roleCodeFor(watchedCreateRole, watchedCreateCustomRoleId)}
                      onValueChange={(code) => {
                        const pick = resolveRolePick(code);
                        createForm.setValue('role', pick.role, {
                          shouldDirty: true,
                          shouldValidate: true,
                        });
                        createForm.setValue('customRoleId', pick.customRoleId ?? undefined, {
                          shouldDirty: true,
                        });
                        createForm.setValue('reportingManagerId', undefined);
                      }}
                      error={error}
                      disabled={pending || rolesQuery.isLoading}
                    />
                  )}
                </UzFormField>
                <UzFormField name="branchId">
                  {({ field, error }) => (
                    <UzSelect<string>
                      name={field.name}
                      label="Branch"
                      placeholder={
                        branchesQuery.isLoading
                          ? 'Loading…'
                          : branchOptions.length === 0
                            ? 'No active branches'
                            : 'Select branch'
                      }
                      options={branchOptions}
                      value={field.value as string | undefined}
                      onValueChange={field.onChange}
                      error={error}
                      hint="Optional. Pick the office / branch this user reports out of."
                      disabled={pending || branchesQuery.isLoading}
                    />
                  )}
                </UzFormField>
                <UzFormField name="subBranchId">
                  {({ field, error }) => (
                    <UzSelect<string>
                      name={field.name}
                      label="Sub-branch"
                      placeholder={
                        !watchedBranchId
                          ? 'Select a branch first'
                          : subBranchesQuery.isLoading
                            ? 'Loading…'
                            : subBranchOptions.length === 0
                              ? 'No sub-branches for this branch'
                              : 'Select sub-branch'
                      }
                      options={subBranchOptions}
                      value={field.value as string | undefined}
                      onValueChange={field.onChange}
                      error={error}
                      hint="Optional. Scoped to the selected branch (Masters -> Sub-branches)."
                      disabled={pending || subBranchesQuery.isLoading || !watchedBranchId}
                    />
                  )}
                </UzFormField>
                <UzFormField name="departmentId">
                  {({ field, error }) => (
                    <UzSelect<string>
                      name={field.name}
                      label="Department"
                      required
                      placeholder={
                        departmentsQuery.isLoading
                          ? 'Loading…'
                          : departmentOptions.length === 0
                            ? 'No active departments'
                            : 'Select department'
                      }
                      options={departmentOptions}
                      value={departmentSelectValue(
                        field.value as string | undefined,
                        watchedCreateOpsDesk,
                      )}
                      onValueChange={onDepartmentSelect}
                      error={error}
                      hint="Admin or a sales department shows destinations, hauls, and trip types. An Operations desk puts the user in that ops rotation."
                      disabled={pending || departmentsQuery.isLoading}
                    />
                  )}
                </UzFormField>
                {/* City + destinations multi-pick: kept in this position
              (before reporting manager) so the cascade-style read
              flows naturally for the operator. */}
                {/* 2026-06-27. "Primary destination city" picker removed.
              The Sales coverage panel below now declares destination
              coverage via the 3 ordered slots (ADR-0057); the
              standalone city FK was redundant. */}
                {showSalesCoverage ? (
                  <SalesCoveragePanel
                    scopes={watchScopes()}
                    onScopesChange={setScopes}
                    hauls={watchHauls()}
                    onHaulsChange={setHauls}
                    tripTypeIds={watchTripTypeIds()}
                    onTripTypeIdsChange={setTripTypeIds}
                    tripTypeOptions={tripTypeOptionsForPanel}
                    tripTypesLoading={tripTypesQuery.isLoading}
                    destinations={watchDestinations()}
                    onDestinationsChange={setDestinations}
                    savedDestinationLabels={savedDestinationLabels}
                    destinationsError={destinationFieldError}
                    scopesError={scopesFieldError}
                    haulsError={haulsFieldError}
                    tripTypesError={tripTypesFieldError}
                    required={coverageRequired}
                    opsMode={opsCoverage}
                    disabled={pending}
                    enabled={open}
                  />
                ) : (
                  <p className="text-xs text-zinc-500">
                    Destinations, hauls, and trip types appear for Sales Lead and Sales Agent roles,
                    and when the department is Admin, Sales, Domestic Sales, or International Sales.
                  </p>
                )}
                {watchedRole && watchedRole !== 'ADMIN' && (
                  <UzFormField name="reportingManagerId">
                    {({ field, error }) => (
                      <UzSelect<string>
                        name={field.name}
                        label="Reporting manager"
                        required
                        placeholder={
                          eligibleManagers.isLoading
                            ? 'Loading…'
                            : eligibleManagers.isError
                              ? 'Could not load managers- try re-selecting the role'
                              : managerOptions.length === 0
                                ? `No active ${parentRoleLabel ?? 'manager'} to assign`
                                : `Pick a ${parentRoleLabel ?? 'manager'}`
                        }
                        options={managerOptions}
                        value={field.value as string | undefined}
                        onValueChange={field.onChange}
                        error={
                          error ?? (eligibleManagers.isError ? eligibleManagersError : undefined)
                        }
                        hint={parentRoleLabel ? `Must be a ${parentRoleLabel} user.` : undefined}
                        disabled={
                          pending || eligibleManagers.isLoading || managerOptions.length === 0
                        }
                      />
                    )}
                  </UzFormField>
                )}
              </div>
            </div>
          </UzForm>
        ) : (
          <UzForm form={updateForm} onSubmit={onUpdate}>
            <div className="grid gap-x-8 gap-y-4 lg:grid-cols-2">
              <div className="space-y-4">
                <h3 className="text-xs font-semibold tracking-wide text-zinc-500 uppercase">
                  Account &amp; profile
                </h3>
                <UzInput
                  label="Work email"
                  value={user?.email ?? ''}
                  hint="Email cannot be changed here."
                  disabled
                  readOnly
                />
                <UzFormField name="fullName">
                  {({ field, error }) => (
                    <UzInput {...field} label="Full name" error={error} disabled={pending} />
                  )}
                </UzFormField>
                <PhoneField pending={pending} />
                <CallerDeskNumberField pending={pending} />
                <UzFormField name="employeeCode">
                  {({ field, error }) => (
                    <UzInput
                      {...field}
                      value={field.value ?? ''}
                      label="Employee code"
                      placeholder="UTZ010726/9875623689"
                      hint="Auto-generated on create. Editable when you need to correct it."
                      error={error}
                      disabled={pending}
                    />
                  )}
                </UzFormField>
                <UserHrFields pending={pending} />
              </div>
              <div className="space-y-4">
                <h3 className="text-xs font-semibold tracking-wide text-zinc-500 uppercase">
                  Role &amp; coverage
                </h3>
                <UzFormField name="role">
                  {({ error }) => (
                    <UzSelect<string>
                      name="role"
                      label="Primary role"
                      options={roleSelectOptions}
                      value={roleCodeFor(watchedUpdateRole, watchedUpdateCustomRoleId)}
                      onValueChange={(code) => {
                        const pick = resolveRolePick(code);
                        updateForm.setValue('role', pick.role, {
                          shouldDirty: true,
                          shouldValidate: true,
                        });
                        updateForm.setValue('customRoleId', pick.customRoleId ?? undefined, {
                          shouldDirty: true,
                        });
                        // Clear the reporting manager only on a real operator switch
                        // (the previous manager may not be valid under the new role's
                        // parent). Done here, not in a watchedRole effect, so initial
                        // hydration never wipes the hydrated manager.
                        updateForm.setValue('reportingManagerId', null);
                      }}
                      error={error}
                      disabled={pending || rolesQuery.isLoading}
                    />
                  )}
                </UzFormField>
                <UzFormField name="branchId">
                  {({ field, error }) => (
                    <UzSelect<string>
                      name={field.name}
                      label="Branch"
                      placeholder={
                        branchesQuery.isLoading
                          ? 'Loading…'
                          : branchOptions.length === 0
                            ? 'No active branches'
                            : 'Select branch'
                      }
                      options={branchOptions}
                      value={(field.value as string | null | undefined) ?? undefined}
                      onValueChange={field.onChange}
                      error={error}
                      disabled={pending || branchesQuery.isLoading}
                    />
                  )}
                </UzFormField>
                <UzFormField name="subBranchId">
                  {({ field, error }) => (
                    <UzSelect<string>
                      name={field.name}
                      label="Sub-branch"
                      placeholder={
                        !watchedBranchId
                          ? 'Select a branch first'
                          : subBranchesQuery.isLoading
                            ? 'Loading…'
                            : subBranchOptions.length === 0
                              ? 'No sub-branches for this branch'
                              : 'Select sub-branch'
                      }
                      options={subBranchOptions}
                      value={(field.value as string | null | undefined) ?? undefined}
                      onValueChange={field.onChange}
                      error={error}
                      hint="Scoped to the selected branch (Masters -> Sub-branches)."
                      disabled={pending || subBranchesQuery.isLoading || !watchedBranchId}
                    />
                  )}
                </UzFormField>
                <UzFormField name="departmentId">
                  {({ field, error }) => (
                    <UzSelect<string>
                      name={field.name}
                      label="Department"
                      required
                      placeholder={
                        departmentsQuery.isLoading
                          ? 'Loading…'
                          : departmentOptions.length === 0
                            ? 'No active departments'
                            : 'Select department'
                      }
                      options={departmentOptions}
                      value={departmentSelectValue(
                        field.value as string | null | undefined,
                        watchedUpdateOpsDesk,
                      )}
                      onValueChange={onDepartmentSelect}
                      error={error}
                      hint="Admin or a sales department shows destinations, hauls, and trip types. An Operations desk puts the user in that ops rotation."
                      disabled={pending || departmentsQuery.isLoading}
                    />
                  )}
                </UzFormField>
                {/* 2026-06-27. "Primary destination city" picker removed -
              see equivalent comment in the create branch. */}
                {showSalesCoverage ? (
                  <SalesCoveragePanel
                    scopes={watchScopes()}
                    onScopesChange={setScopes}
                    hauls={watchHauls()}
                    onHaulsChange={setHauls}
                    tripTypeIds={watchTripTypeIds()}
                    onTripTypeIdsChange={setTripTypeIds}
                    tripTypeOptions={tripTypeOptionsForPanel}
                    tripTypesLoading={tripTypesQuery.isLoading}
                    destinations={watchDestinations()}
                    onDestinationsChange={setDestinations}
                    savedDestinationLabels={savedDestinationLabels}
                    destinationsError={destinationFieldError}
                    scopesError={scopesFieldError}
                    haulsError={haulsFieldError}
                    tripTypesError={tripTypesFieldError}
                    required={coverageRequired}
                    opsMode={opsCoverage}
                    disabled={pending}
                    enabled={open}
                  />
                ) : (
                  <p className="text-xs text-zinc-500">
                    Destinations, hauls, and trip types appear for Sales Lead and Sales Agent roles,
                    and when the department is Admin, Sales, Domestic Sales, or International Sales.
                  </p>
                )}
                {watchedRole && watchedRole !== 'ADMIN' && (
                  <UzFormField name="reportingManagerId">
                    {({ field, error }) => (
                      <UzSelect<string>
                        name={field.name}
                        label="Reporting manager"
                        required
                        placeholder={
                          eligibleManagers.isLoading
                            ? 'Loading…'
                            : eligibleManagers.isError
                              ? 'Could not load managers - try re-selecting the role'
                              : managerOptions.length === 0
                                ? `No active ${parentRoleLabel ?? 'manager'} to assign`
                                : `Pick a ${parentRoleLabel ?? 'manager'}`
                        }
                        options={managerOptions}
                        value={(field.value as string | null | undefined) ?? undefined}
                        onValueChange={field.onChange}
                        error={
                          error ?? (eligibleManagers.isError ? eligibleManagersError : undefined)
                        }
                        hint={parentRoleLabel ? `Must be a ${parentRoleLabel} user.` : undefined}
                        disabled={
                          pending || eligibleManagers.isLoading || managerOptions.length === 0
                        }
                      />
                    )}
                  </UzFormField>
                )}
              </div>
            </div>
          </UzForm>
        )}
      </UzDialog>
      <ExitReleaseConfirmDialog
        open={pendingExitPayload !== null}
        userId={user?.id ?? null}
        userName={user?.fullName ?? ''}
        action="date_of_exit"
        onOpenChange={(o) => {
          // Cancel keeps the drawer open so the operator can revise the date.
          if (!o) setPendingExitPayload(null);
        }}
        onConfirm={async () => {
          if (!user || !pendingExitPayload) return;
          await updateUser.mutateAsync({ id: user.id, payload: pendingExitPayload });
          setPendingExitPayload(null);
          onOpenChange(false);
        }}
      />
    </>
  );
};

// ----- SalesCoveragePanel ------------------------------------------------
//
// 2026-06-26 ADR-0057. Renders the four FIRST-CLASS sales-coverage
// inputs in the order the operator picks them:
//
//   1. International / Domestic  (multi-select pill toggles)
//   2. Short / Long Haul         (multi-select pill toggles)
//   3. Trip types                (multi-select checkbox list, from
//                                 the dynamic TripType master)
//   4. Destinations              (dynamic list: primary required +
//                                 optional additional rows)
//
// All four are INDEPENDENT - no cross-validation between scope/haul/
// trip-type and the picked destinations. The form's superRefine on
// `destinations` enforces "primary required when any picked" + no
// duplicate slots / destinations.

interface SalesCoveragePanelProps {
  scopes: UserSalesScope[];
  onScopesChange: (next: UserSalesScope[]) => void;
  hauls: UserSalesHaul[];
  onHaulsChange: (next: UserSalesHaul[]) => void;
  tripTypeIds: string[];
  onTripTypeIdsChange: (next: string[]) => void;
  tripTypeOptions: ReadonlyArray<{ value: string; label: string }>;
  tripTypesLoading: boolean;
  destinations: UserDestinationSlot[];
  onDestinationsChange: (next: UserDestinationSlot[]) => void;
  savedDestinationLabels?: Record<string, string>;
  destinationsError?: string;
  scopesError?: string;
  haulsError?: string;
  tripTypesError?: string;
  /** Sales roles: every one of the four must have at least one pick. */
  required?: boolean;
  /** Ops roles: scope / haul / destinations only, all optional, no per-day caps. */
  opsMode?: boolean;
  disabled?: boolean;
  enabled?: boolean;
}

const SalesCoveragePanel = ({
  scopes,
  onScopesChange,
  hauls,
  onHaulsChange,
  tripTypeIds,
  onTripTypeIdsChange,
  tripTypeOptions,
  tripTypesLoading,
  destinations,
  onDestinationsChange,
  savedDestinationLabels,
  destinationsError,
  scopesError,
  haulsError,
  tripTypesError,
  required = false,
  opsMode = false,
  disabled,
  enabled = true,
}: SalesCoveragePanelProps): React.JSX.Element => {
  const requiredMark = required ? <span className="text-red-600"> *</span> : null;
  const fieldError = (message: string | undefined): React.JSX.Element | null =>
    message ? <p className="mt-1 text-xs text-red-600">{message}</p> : null;
  const toggleScope = (v: UserSalesScope): void => {
    if (disabled) return;
    onScopesChange(scopes.includes(v) ? scopes.filter((s) => s !== v) : [...scopes, v]);
  };
  const toggleHaul = (v: UserSalesHaul): void => {
    if (disabled) return;
    onHaulsChange(hauls.includes(v) ? hauls.filter((h) => h !== v) : [...hauls, v]);
  };
  const toggleTripType = (id: string): void => {
    if (disabled) return;
    onTripTypeIdsChange(
      tripTypeIds.includes(id) ? tripTypeIds.filter((t) => t !== id) : [...tripTypeIds, id],
    );
  };

  return (
    <div className="space-y-4">
      <label className="text-xs font-medium tracking-wide text-zinc-500 uppercase">
        {opsMode ? 'Operations coverage' : 'Sales coverage'}
      </label>
      {required ? (
        <p className="text-xs text-zinc-500">
          Required for sales roles: pick at least one in each of the four blocks below.
        </p>
      ) : null}
      {opsMode ? (
        <p className="text-xs text-zinc-500">
          Optional, no caps. A converted lead on this user&apos;s desk goes to them when it matches
          any one pick: its scope, its haul, or its destination. No picks = takes every lead on the
          desk.
        </p>
      ) : null}

      {/* 1. Scope (multi pill toggles) */}
      <div>
        <div className="text-xs font-medium text-zinc-700">
          International / Domestic{requiredMark}
        </div>
        <p className="mt-0.5 text-xs text-zinc-500">
          Multi-select. Pick both if the user covers either.
        </p>
        <div className="mt-2 flex flex-wrap gap-2">
          {SCOPE_VALUES.map((v) => {
            const active = scopes.includes(v);
            return (
              <button
                key={v}
                type="button"
                onClick={() => toggleScope(v)}
                disabled={disabled}
                className={
                  'rounded-full border px-3 py-1 text-xs font-medium transition-colors ' +
                  (active
                    ? 'border-indigo-600 bg-indigo-50 text-indigo-700'
                    : 'border-zinc-300 bg-white text-zinc-700 hover:bg-zinc-50') +
                  (disabled ? ' cursor-not-allowed opacity-60' : '')
                }
              >
                {SCOPE_LABELS[v]}
              </button>
            );
          })}
        </div>
        {fieldError(scopesError)}
      </div>

      {/* 2. Haul (multi pill toggles) */}
      <div>
        <div className="text-xs font-medium text-zinc-700">
          Haul{required && salesHaulRequired(scopes) ? requiredMark : null}
        </div>
        <p className="mt-0.5 text-xs text-zinc-500">
          {scopes.length > 0 && !salesHaulRequired(scopes)
            ? 'Not needed for Domestic-only coverage.'
            : 'Multi-select. Pick both if the user covers both.'}
        </p>
        <div className="mt-2 flex flex-wrap gap-2">
          {HAUL_VALUES.map((v) => {
            const active = hauls.includes(v);
            return (
              <button
                key={v}
                type="button"
                onClick={() => toggleHaul(v)}
                disabled={disabled}
                className={
                  'rounded-full border px-3 py-1 text-xs font-medium transition-colors ' +
                  (active
                    ? 'border-indigo-600 bg-indigo-50 text-indigo-700'
                    : 'border-zinc-300 bg-white text-zinc-700 hover:bg-zinc-50') +
                  (disabled ? ' cursor-not-allowed opacity-60' : '')
                }
              >
                {HAUL_LABELS[v]}
              </button>
            );
          })}
        </div>
        {fieldError(haulsError)}
      </div>

      {/* 3. Trip types (multi-checkbox from dynamic master) - not an ops dimension */}
      {opsMode ? null : (
        <div>
          <div className="text-xs font-medium text-zinc-700">Trip types{requiredMark}</div>
          <p className="mt-0.5 text-xs text-zinc-500">
            Multi-select. Pick the trip types this user actively covers - independent of the picked
            destinations.
          </p>
          <div className="mt-2 rounded-md border border-zinc-200 bg-white">
            {tripTypesLoading ? (
              <div className="flex items-center gap-2 p-3 text-sm text-zinc-500">
                <UzSpinner size="sm" /> Loading trip types...
              </div>
            ) : tripTypeOptions.length === 0 ? (
              <div className="p-3 text-sm text-zinc-500">
                No active trip types in the master. Seed the Trip types master to enable this
                picker.
              </div>
            ) : (
              <ul className="divide-y divide-zinc-100">
                {tripTypeOptions.map((t) => {
                  const checked = tripTypeIds.includes(t.value);
                  return (
                    <li key={t.value}>
                      <label
                        className={
                          'flex cursor-pointer items-center gap-2 p-2.5 hover:bg-zinc-50 ' +
                          (disabled ? 'cursor-not-allowed opacity-60' : '')
                        }
                      >
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={() => toggleTripType(t.value)}
                          disabled={disabled}
                          className="h-4 w-4 rounded border-zinc-300"
                        />
                        <span className="text-sm text-zinc-900">{t.label}</span>
                      </label>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
          {fieldError(tripTypesError)}
        </div>
      )}

      {/* 4. Destinations (dynamic list) */}
      <div>
        <div className="text-xs font-medium text-zinc-700">Destinations{requiredMark}</div>
        <p className="mt-0.5 text-xs text-zinc-500">
          {opsMode
            ? 'Optional. Add rows (P1, P2, P3, …) as needed. A mapped destination alone is enough for the user to take its converted leads on their desk.'
            : `${required ? 'At least one is required. ' : 'Optional. '}Add rows (P1, P2, P3, …) as needed - each pick is independent of scope, haul, and trip types above. A mapped destination is enough for the user to take its leads. Marketing / day is shown on Marketing Reports; LPS / day caps LPS auto-assignment for that destination (blank = no LPS leads).`}
        </p>
        <div className="mt-2">
          <UserDestinationsEditor
            value={destinations}
            onChange={onDestinationsChange}
            savedLabels={savedDestinationLabels}
            disabled={disabled}
            enabled={enabled}
            error={destinationsError}
            hideCapacity={opsMode}
          />
        </div>
      </div>
    </div>
  );
};
